import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import c from 'classnames';

import { Icon } from '@repo/ui';
import { Kit } from '@repo/types';
import { KitStep } from 'views/Kits/ActivateKit';
import { ActivationHeader } from 'components';
import { useActivationSteps } from 'hooks/useActivationSteps';
import useFeature from 'services/features/useFeature';

import styles from './Kits.module.css';

export type ActivationNavigationProps = {
  startingStep?: number;
  step: number;
  steps: readonly KitStep[];
  setCurrentActivationStep: (step: number) => void;
  kitId?: string;
  kit?: Partial<Kit>;
};

/**
 * Back/close/stepper state of the activation flow, shared by the legacy header
 * (ActivationNavigation) and the redesigned ActivationScreen in ActivateKit.
 */
export const useActivationNavigation = ({
  step,
  setCurrentActivationStep,
  startingStep,
  steps,
  kitId,
  kit,
}: ActivationNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const mandatorySteps = useMemo(
    () => steps.filter(step => !step.optional),
    [steps],
  );
  const isOptionalStep = !!steps[step]?.optional;
  // The kit is activated when sample details are submitted, so from the next
  // step on there is no earlier screen to go back to.
  const activationStepIndex = steps.findIndex(
    s => s.id === 'sampleDetailsStep',
  );
  const isAfterActivation =
    activationStepIndex !== -1 && step > activationStepIndex;
  const activationSteps = useActivationSteps(kit);

  const stepStage = steps[step]?.stage ?? 'activate';
  const currentStepKey = activationSteps.some(s => s.key === stepStage)
    ? stepStage
    : 'activate';

  const navigateBack = () => {
    // second should navigate to /activate when Back is pressed since the 1st step is mostly "automatically completed"
    if (step === (startingStep ?? 0) || step === (startingStep ?? 0) + 1) {
      if (state?.from) {
        navigate(state.from);
      } else {
        navigate('/activate');
      }
    } else {
      setCurrentActivationStep(step - 1);
    }
  };
  const redirectToResults = () => {
    kitId ? navigate(`/results/${kitId}`) : navigate('/results');
  };

  return {
    mandatorySteps,
    isOptionalStep,
    isAfterActivation,
    activationSteps,
    currentStepKey,
    navigateBack,
    redirectToResults,
  };
};

export const ActivationNavigation = (props: ActivationNavigationProps) => {
  const { step } = props;
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const {
    mandatorySteps,
    isOptionalStep,
    activationSteps,
    currentStepKey,
    navigateBack,
    redirectToResults,
  } = useActivationNavigation(props);

  if (isRedesignedActivationEnabled) {
    return (
      <ActivationHeader
        steps={activationSteps}
        currentStepKey={currentStepKey}
        onBack={navigateBack}
      />
    );
  }

  return (
    <div className={styles.activationNavigation}>
      <div className={styles.navPlaceholder}>
        {!isOptionalStep && (
          <div className={styles.backButton} onClick={navigateBack}>
            <Icon name='chevronBack' />
            Back
          </div>
        )}
      </div>
      <div className={styles.stepsProgress}>
        {mandatorySteps.map((s, idx) => (
          <div
            className={c(
              styles.progressMarker,
              idx < step && styles.filled,
              idx === step && styles.partial,
            )}
            key={idx}
            data-testid='progressMarker'
          ></div>
        ))}
        {isOptionalStep && (
          <div
            className={c(styles.progressMarker, styles.partial)}
            key={step}
            data-testid='progressMarker'
          ></div>
        )}
      </div>
      <div className={styles.navPlaceholder}>
        {isOptionalStep && (
          <div
            className={styles.closeButton}
            onClick={() => redirectToResults()}
          >
            <Icon name='closeSm' />
          </div>
        )}
      </div>
    </div>
  );
};
